import React, { useEffect } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import Unibrow from 'js/design_system/Unibrow';
import { recordShow } from 'js/utils/dismissibles';
import Timer from './Timer';
import useDiscountDuration from './useDiscountDuration';

import type { SeasonalDiscountUnibrowProps } from 'js/unibrows/types';

type MinibrowProps = {
  className?: string;
  // End month and day as a timestamp or in the form "MM/DD", no year
  endDate?: string;
};

const TimerGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
`;

const Message = styled.h3`
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 0.03em;
  margin: 0;
  opacity: 0.8;
  text-transform: uppercase;
`;

const MinibrowContent = styled.div`
  background: #0954d3;
  height: 85px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const MinibrowTextBlock = styled.div`
  padding-top: 15px;
`;

const MinibrowSubtitle = styled.h2`
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  margin: 0 0 3px;
  opacity: 0.8;
  text-transform: uppercase;
`;

const MinibrowTitle = styled.h1`
  color: #fff;
  font-size: 14px;
  font-weight: 400;
  margin: 0;
`;

export const SeasonalDiscountMinibrow = ({
  className,
  endDate,
}: MinibrowProps) => {
  const duration = useDiscountDuration(endDate);

  useEffect(() => {
    recordShow('SeasonalDiscountMinibrow');
  }, []);

  return (
    <div>
      <MinibrowContent className={className}>
        <MinibrowTextBlock>
          <MinibrowSubtitle>Limited Time</MinibrowSubtitle>
          <MinibrowTitle>Offer ends soon</MinibrowTitle>
        </MinibrowTextBlock>
        <Timer startDuration={duration} mini />
      </MinibrowContent>
    </div>
  );
};

const SeasonalDiscountUnibrow = ({
  couponCode,
  lctDiscountVariant,
  endDate,
  feature,
  trigger,
  trialDays,
  trialPrice,
  couponAmount,
}: SeasonalDiscountUnibrowProps) => {
  const duration = useDiscountDuration(endDate);
  const isLctNoDiscount = lctDiscountVariant === 'lct_no_discount';

  const interpolateCopy = (copy: string) =>
    copy
      .replace('{TRIAL_DAYS}', trialDays?.toString() ?? '')
      .replace('{TRIAL_PRICE}', trialPrice ?? '')
      .replace('{COUPON_PERCENT}', couponAmount?.toString() ?? '');

  const discountCopy = interpolateCopy(
    isLctNoDiscount
      ? 'Academia Premium {TRIAL_DAYS} day trial for just {TRIAL_PRICE}'
      : '{COUPON_PERCENT}% OFF ACADEMIA PREMIUM',
  );
  const limitedTimeCopy = interpolateCopy(
    isLctNoDiscount ? 'Don\u2019t forget:' : 'Limited Time',
  );
  const endingSoonCopy = isLctNoDiscount ? '' : 'OFFER ENDS SOON';
  const ctaCopy = interpolateCopy(
    isLctNoDiscount ? 'Upgrade now for {TRIAL_PRICE}' : 'Upgrade Now',
  );
  const hideTimer = isLctNoDiscount;

  const handleClick = () => {
    const url = couponCode
      ? routes.upgrade_url({
          feature,
          trigger,
          premium_discount_coupon: couponCode,
        })
      : routes.upgrade_url({
          feature,
          trigger,
        });
    window.location.href = url;
  };

  const timerContent = !hideTimer ? (
    <TimerGroup>
      <Message>{endingSoonCopy}</Message>
      <Timer startDuration={duration} />
    </TimerGroup>
  ) : undefined;

  return (
    <Unibrow
      dismissibleName="SeasonalDiscountUnibrow"
      eyebrowIcon="campaign"
      eyebrowText={limitedTimeCopy}
      text={discountCopy}
      primaryButton={{ text: ctaCopy, onClick: handleClick }}
      content={timerContent}
    />
  );
};

export default SeasonalDiscountUnibrow;
